import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { fetchWatchlist, toggleWatchlist, getUserToken } from "../lib/api";
import { PropCard } from "../components/PropCard";
import { Star, Copy } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Watchlist() {
  const [props, setProps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [watched, setWatched] = useState(new Set());
  const token = getUserToken();

  const load = useCallback(async () => {
    setLoading(true);
    const data = await fetchWatchlist(token);
    setProps(data.props);
    setWatched(new Set(data.props.map((p) => p.id)));
    setLoading(false);
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const handleWatch = async (propId) => {
    await toggleWatchlist(token, propId);
    toast("Removed from My Picks");
    load();
  };

  const handleCopy = () => {
    const text = props
      .map((p) => {
        const player = p.player?.name || "?";
        return `${p.pick} ${p.line} ${p.stat_type} — ${player} (${p.confidence}%)`;
      })
      .join("\n");
    if (!text) return;
    navigator.clipboard.writeText(text);
    toast("Picks copied to clipboard", { description: "Paste anywhere to share your slate." });
  };

  return (
    <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10">
      <div className="overline mb-2">Your Selected Picks</div>
      <div className="flex items-center justify-between border-b border-[#27272A] pb-6 mb-8 flex-wrap gap-3">
        <div>
          <h1 className="font-head text-4xl text-white tracking-tight">MY PICKS</h1>
          <p className="text-[#A1A1AA] text-sm mt-1">Hand-picked over/under selections. Each pick stands alone — no parlays, no bundling.</p>
        </div>
        <div className="flex items-center gap-3">
          {props.length > 0 && (
            <button
              onClick={handleCopy}
              className="flex items-center gap-2 px-3 h-10 border border-[#27272A] hover:border-white/30 transition-colors"
              data-testid="copy-picks-btn"
            >
              <Copy size={14} />
              <span className="overline">Copy slate</span>
            </button>
          )}
          <div className="font-data text-2xl text-white" data-testid="picks-total">{props.length}</div>
        </div>
      </div>
      {loading ? (
        <div className="text-[#A1A1AA] py-20 text-center" data-testid="watchlist-loading">Loading…</div>
      ) : props.length === 0 ? (
        <div className="text-center py-20" data-testid="watchlist-empty">
          <Star size={32} color="#52525b" />
          <div className="text-[#A1A1AA] mt-4 mb-2">You haven't picked anything yet.</div>
          <Link to="/" className="overline text-[#FF4500] hover:underline" data-testid="watchlist-cta">
            Browse the board →
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="watchlist-grid">
          {props.map((p) => (
            <PropCard key={p.id} prop={p} onToggleWatch={handleWatch} isWatched={watched.has(p.id)} />
          ))}
        </div>
      )}
    </div>
  );
}
